import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API } from "../data/site";

const AuthContext = createContext(null);

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Session is only relevant on admin routes — skip the probe on public pages
  // so anonymous visitors never trigger the guaranteed 401 round-trip.
  useEffect(() => {
    if (!location.pathname.startsWith("/admin")) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!cancelled) setUser(res.ok ? await res.json() : false);
      } catch {
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
    setUser(data);
    return data;
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  if (loading || user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy" data-testid="admin-auth-loading">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-400">Checking session…</p>
      </div>
    );
  }
  if (!user) {
    navigate(`/admin/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
    return null;
  }
  return children;
};
